import React, { useState } from "react";
import { motion } from "framer-motion";

const services = [
  {
    title: "Video Editor",
    description: "Delivers professional edits, motion graphics, and post-production storytelling.",
    icon: "🎬",
    skills: ["Premiere Pro", "After Effects", "Final Cut Pro"],
  },
  {
    title: "Graphic Designer",
    description: "Creates visual concepts and designs for branding, ads, and print.",
    icon: "🎨",
    skills: ["Photoshop", "Illustrator", "InDesign"],
  },
  {
    title: "Web Developer",
    description: "Builds responsive, interactive, and high-performance websites.",
    icon: "💻",
    skills: ["HTML", "CSS", "JavaScript", "React"],
  },
  {
    title: "UI/UX Designer",
    description: "Designs intuitive and engaging user interfaces and experiences.",
    icon: "🖥️",
    skills: ["Figma", "Sketch", "Adobe XD"],
  },
  {
    title: "Mobile App Developer",
    description: "Develops feature-rich mobile apps for Android and iOS.",
    icon: "📱",
    skills: ["Flutter", "React Native", "Swift"],
  },
  {
    title: "Content Writer",
    description: "Crafts compelling blog posts, marketing copy, and SEO content.",
    icon: "✍️",
    skills: ["SEO", "Copywriting", "Editing"],
  },
];

const ServiceSection = () => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const servicesPerPage = 3;

  const filteredServices = services.filter(
    (service) =>
      service.title.toLowerCase().includes(search.toLowerCase()) ||
      service.skills.some((skill) =>
        skill.toLowerCase().includes(search.toLowerCase())
      )
  );

  const totalPages = Math.ceil(filteredServices.length / servicesPerPage);
  const startIndex = (currentPage - 1) * servicesPerPage;
  const currentServices = filteredServices.slice(
    startIndex,
    startIndex + servicesPerPage
  );

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  return (
    <section className="bg-gray-50 px-4 py-8 md:py-10 lg:py-12">
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
          Service
        </h2>
        <p className="text-gray-600 text-sm md:text-base mb-8">
          Connect with top professionals in various fields
        </p>

        <div className="flex justify-center mb-6">
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Search by skill or role..."
            className="border border-gray-300 rounded-lg px-4 py-2 w-full max-w-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {search && (
          <p className="text-sm text-gray-500 mt-1 mb-3">
            Showing results for "<span className="font-medium">{search}</span>"
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {currentServices.length ? (
            currentServices.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition duration-300 flex flex-col items-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="text-4xl mb-4">{service.icon}</div>
                <h4 className="text-md font-semibold text-gray-800 mb-2">
                  {service.title}
                </h4>
                <p className="text-gray-600 text-sm text-center mb-4">
                  {service.description}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {service.skills.map((skill, idx) => (
                    <span
                      key={idx}
                      className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))
          ) : (
            <p className="col-span-full text-gray-500 text-sm">
              No services found.
            </p>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-10 gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              className="w-8 h-8 rounded-full bg-white border hover:bg-gray-100"
            >
              ‹
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-2.5 h-2.5 rounded-full ${
                  currentPage === i + 1 ? "bg-blue-700" : "bg-gray-400"
                }`}
              ></button>
            ))}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              className="w-8 h-8 rounded-full bg-white border hover:bg-gray-100"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceSection;
